import React, { useEffect, useMemo, useRef, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { supabase } from '../services/supabaseClient';
import {
  FaLocationArrow,
  FaUnlockAlt,
  FaWalking,
  FaQrcode,
  FaTimes,
  FaSearch,
  FaCheckCircle,
  FaMapMarkerAlt,
  FaRoute,
  FaCompass,
  FaMap,
  FaCrosshairs,
  FaFilter,
  FaListUl,
  FaStore,
  FaExternalLinkAlt
} from 'react-icons/fa';
import {
  GoogleMap,
  Marker,
  Circle,
  InfoWindow,
  useJsApiLoader
} from '@react-google-maps/api';
import T from '../components/i18n/T';
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_AUTH_REFERRER_POLICY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
  GOOGLE_MAPS_SCRIPT_ID,
} from '../lib/googleMapsConfig';

type BusinessLite = {
  id: string;
  name?: string | null;
  image_url?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  region?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

type DropItem = {
  id: string;
  business_id: string;
  title: string;
  description: string | null;
  reward: string;
  radius_meters: number;
  latitude: number;
  longitude: number;
  is_active: boolean;

  business_name?: string | null;
  business_address?: string | null;
  business_city?: string | null;
  business_postal_code?: string | null;
  business_region?: string | null;

  drop_address?: string | null;
  drop_city?: string | null;
  drop_postal_code?: string | null;
  drop_region?: string | null;
  drop_country?: string | null;

  created_at?: string;
  updated_at?: string;
  currentDistance?: number;
  businesses?: BusinessLite | null;
};

type FilterMode = 'all' | 'unlocked' | 'hot' | 'near';

function getDistanceInMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371e3;
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaPhi = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
    Math.cos(phi1) *
      Math.cos(phi2) *
      Math.sin(deltaLambda / 2) *
      Math.sin(deltaLambda / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
}

function getHeatLabel(distance: number, radius: number) {
  if (distance <= radius) {
    return { label: 'Débloqué', color: 'text-green-400', bg: 'bg-green-500/20' };
  }
  if (distance <= Math.max(radius * 2, 100)) {
    return { label: 'Très chaud', color: 'text-orange-300', bg: 'bg-orange-500/20' };
  }
  if (distance <= Math.max(radius * 4, 250)) {
    return { label: 'Chaud', color: 'text-yellow-300', bg: 'bg-yellow-500/20' };
  }
  if (distance <= Math.max(radius * 8, 500)) {
    return { label: 'Proche', color: 'text-cyan-300', bg: 'bg-cyan-500/20' };
  }
  return { label: 'Loin', color: 'text-slate-300', bg: 'bg-white/10' };
}

function formatDistance(distance?: number) {
  if (distance === undefined || distance === null) return '—';
  if (distance < 1000) return `${distance} m`;
  return `${(distance / 1000).toFixed(1)} km`;
}

function isHotDrop(drop: DropItem) {
  if (drop.currentDistance === undefined) return false;
  return drop.currentDistance <= Math.max(drop.radius_meters * 2, 100);
}

function isNearDrop(drop: DropItem) {
  if (drop.currentDistance === undefined) return false;
  return drop.currentDistance <= Math.max(drop.radius_meters * 8, 500);
}

function getBestCoords(drop: DropItem) {
  if (
    typeof drop.latitude === 'number' &&
    typeof drop.longitude === 'number' &&
    Number.isFinite(drop.latitude) &&
    Number.isFinite(drop.longitude)
  ) {
    return { lat: drop.latitude, lng: drop.longitude };
  }

  const businessLat = drop.businesses?.latitude;
  const businessLng = drop.businesses?.longitude;

  if (
    typeof businessLat === 'number' &&
    typeof businessLng === 'number' &&
    Number.isFinite(businessLat) &&
    Number.isFinite(businessLng)
  ) {
    return { lat: businessLat, lng: businessLng };
  }

  return { lat: 46.8139, lng: -71.2080 };
}

function getBusinessAddress(drop: DropItem) {
  const dropParts = [
    drop.drop_address,
    drop.drop_city,
    drop.drop_postal_code,
    drop.drop_region,
    drop.drop_country
  ].filter(Boolean);

  if (dropParts.length > 0) {
    return dropParts.join(', ');
  }

  const businessParts = [
    drop.business_address || drop.businesses?.address,
    drop.business_city || drop.businesses?.city,
    drop.business_postal_code || drop.businesses?.postal_code,
    drop.business_region || drop.businesses?.region
  ].filter(Boolean);

  return businessParts.join(', ');
}

function getBusinessDisplayName(drop: DropItem) {
  return drop.business_name || drop.businesses?.name || 'Commerce partenaire';
}

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

export default function ForfeoDrops() {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [drops, setDrops] = useState<DropItem[]>([]);
  const [loadingDrops, setLoadingDrops] = useState(true);
  const [error, setError] = useState('');
  const [unlockedDrop, setUnlockedDrop] = useState<DropItem | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [closestDistance, setClosestDistance] = useState<number | null>(null);
  const [selectedDrop, setSelectedDrop] = useState<DropItem | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  const watchIdRef = useRef<number | null>(null);
  const unlockedIdsRef = useRef<Set<string>>(new Set());

  const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: GOOGLE_MAPS_LANGUAGE,
    region: GOOGLE_MAPS_REGION,
    authReferrerPolicy: GOOGLE_MAPS_AUTH_REFERRER_POLICY,
  });

  useEffect(() => {
    const fetchDrops = async () => {
      setLoadingDrops(true);
      setError('');

      try {
        // 1) Charger les drops sans jointure PostgREST
        const { data: dropsData, error: dropsError } = await supabase
          .from('forfeo_drops')
          .select(`
            id,
            business_id,
            title,
            description,
            reward,
            radius_meters,
            latitude,
            longitude,
            is_active,
            business_name,
            business_address,
            business_city,
            business_postal_code,
            business_region,
            drop_address,
            drop_city,
            drop_postal_code,
            drop_region,
            drop_country,
            created_at,
            updated_at
          `)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (dropsError) {
          console.error('Erreur fetch drops:', dropsError);
          setError("Impossible de charger les drops pour le moment.");
          setDrops([]);
          setLoadingDrops(false);
          return;
        }

        const safeDrops = ((dropsData || []) as DropItem[]).map((drop) => ({
          ...drop,
          businesses: null
        }));

        // 2) Charger les commerces liés séparément
        const businessIds = Array.from(
          new Set(
            safeDrops
              .map((d) => d.business_id)
              .filter((id): id is string => typeof id === 'string' && id.length > 0)
          )
        );

        let businessMap = new Map<string, BusinessLite>();

        if (businessIds.length > 0) {
          const { data: businessesData, error: businessesError } = await supabase
            .from('businesses')
            .select(`
              id,
              name,
              image_url,
              address,
              city,
              postal_code,
              region,
              latitude,
              longitude
            `)
            .in('id', businessIds);

          if (businessesError) {
            console.warn('Impossible de charger les commerces liés aux drops:', businessesError);
          } else {
            businessMap = new Map(
              ((businessesData || []) as BusinessLite[]).map((b) => [b.id, b])
            );
          }
        }

        // 3) Fusionner les données pour conserver 100% du comportement actuel
        const mergedDrops: DropItem[] = safeDrops.map((drop) => ({
          ...drop,
          businesses: businessMap.get(drop.business_id) || null,
          business_name: drop.business_name || businessMap.get(drop.business_id)?.name || null,
          business_address:
            drop.business_address || businessMap.get(drop.business_id)?.address || null,
          business_city: drop.business_city || businessMap.get(drop.business_id)?.city || null,
          business_postal_code:
            drop.business_postal_code || businessMap.get(drop.business_id)?.postal_code || null,
          business_region:
            drop.business_region || businessMap.get(drop.business_id)?.region || null
        }));

        setDrops(mergedDrops);
      } catch (err) {
        console.error('Erreur inattendue fetch drops:', err);
        setError("Impossible de charger les drops pour le moment.");
        setDrops([]);
      } finally {
        setLoadingDrops(false);
      }
    };

    fetchDrops();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  const startRadar = () => {
    setError('');
    setUnlockedDrop(null);
    setIsScanning(true);

    if (!navigator.geolocation) {
      setError("La géolocalisation n'est pas supportée par votre navigateur.");
      setIsScanning(false);
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      () => {
        setError("Veuillez autoriser l'accès à votre position pour détecter les drops.");
        setIsScanning(false);
      },
      {
        enableHighAccuracy: true,
        maximumAge: 10000,
        timeout: 10000
      }
    );
  };

  const stopRadar = () => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setIsScanning(false);
    setUserLocation(null);
    setClosestDistance(null);
  };

  useEffect(() => {
    if (!userLocation || drops.length === 0) return;

    let closest: number | null = null;
    let foundUnlocked: DropItem | null = null;

    const enriched = drops.map((drop) => {
      const coords = getBestCoords(drop);
      const distance = getDistanceInMeters(
        userLocation.lat,
        userLocation.lng,
        coords.lat,
        coords.lng
      );

      if (closest === null || distance < closest) closest = distance;

      if (
        distance <= drop.radius_meters &&
        !unlockedIdsRef.current.has(drop.id) &&
        !foundUnlocked
      ) {
        foundUnlocked = { ...drop, currentDistance: distance };
      }

      return { ...drop, currentDistance: distance };
    });

    enriched.sort((a, b) => (a.currentDistance || 999999) - (b.currentDistance || 999999));

    setDrops(enriched);
    setClosestDistance(closest);

    if (foundUnlocked) {
      unlockedIdsRef.current.add(foundUnlocked.id);
      setUnlockedDrop(foundUnlocked);
      setSelectedDrop(foundUnlocked);
      if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
      stopRadar();
    }
  }, [userLocation]);

  const sortedDrops = useMemo(() => {
    return [...drops].sort((a, b) => (a.currentDistance || 999999) - (b.currentDistance || 999999));
  }, [drops]);

  const filteredDrops = useMemo(() => {
    if (filterMode === 'unlocked') {
      return sortedDrops.filter(
        d => d.currentDistance !== undefined && d.currentDistance <= d.radius_meters
      );
    }
    if (filterMode === 'hot') {
      return sortedDrops.filter(d => isHotDrop(d));
    }
    if (filterMode === 'near') {
      return sortedDrops.filter(d => isNearDrop(d));
    }
    return sortedDrops;
  }, [sortedDrops, filterMode]);

  const stats = useMemo(() => {
    return {
      total: drops.length,
      unlocked: drops.filter(
        d => d.currentDistance !== undefined && d.currentDistance <= d.radius_meters
      ).length,
      hot: drops.filter(d => isHotDrop(d)).length,
      near: drops.filter(d => isNearDrop(d)).length,
    };
  }, [drops]);

  const mapCenter = useMemo(() => {
    if (userLocation) return userLocation;
    if (sortedDrops.length > 0) {
      const coords = getBestCoords(sortedDrops[0]);
      return { lat: coords.lat, lng: coords.lng };
    }
    return { lat: 46.8139, lng: -71.2080 };
  }, [userLocation, sortedDrops]);

  const openGoogleMapsPin = (drop: DropItem) => {
    const coords = getBestCoords(drop);
    const address = getBusinessAddress(drop);

    const query = address
      ? encodeURIComponent(address)
      : encodeURIComponent(`${coords.lat},${coords.lng}`);

    const url = `https://www.google.com/maps/search/?api=1&query=${query}`;
    window.open(url, '_blank');
  };

  const openGoogleMapsDirections = (drop: DropItem) => {
    const coords = getBestCoords(drop);
    const address = getBusinessAddress(drop);

    const destination = address
      ? encodeURIComponent(address)
      : encodeURIComponent(`${coords.lat},${coords.lng}`);

    const url = userLocation
      ? `https://www.google.com/maps/dir/?api=1&origin=${userLocation.lat},${userLocation.lng}&destination=${destination}&travelmode=walking`
      : `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=walking`;

    window.open(url, '_blank');
  };

  const centerOnUser = () => {
    if (!mapInstance || !userLocation) return;
    mapInstance.panTo(userLocation);
    mapInstance.setZoom(16);
  };

  const focusDropOnMap = (drop: DropItem) => {
    const coords = getBestCoords(drop);
    setSelectedDrop(drop);
    if (!mapInstance) return;
    mapInstance.panTo({ lat: coords.lat, lng: coords.lng });
    mapInstance.setZoom(17);
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] font-sans text-white overflow-hidden flex flex-col">
      <Header />

      <div className="flex-1 relative px-4 pt-28 pb-16">
        {isScanning && !unlockedDrop && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
            <div
              className="w-[18rem] h-[18rem] border border-cyan-500 rounded-full absolute animate-ping"
              style={{ animationDuration: '3s' }}
            />
            <div
              className="w-[28rem] h-[28rem] border border-cyan-500/50 rounded-full absolute animate-ping"
              style={{ animationDuration: '3s', animationDelay: '1s' }}
            />
            <div
              className="w-[38rem] h-[38rem] border border-cyan-500/20 rounded-full absolute animate-ping"
              style={{ animationDuration: '3s', animationDelay: '2s' }}
            />
          </div>
        )}

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
            <div className="xl:col-span-3">
              {!unlockedDrop ? (
                <div className="bg-white/5 backdrop-blur-xl p-8 rounded-3xl border border-white/10 shadow-2xl sticky top-28">
                  <div className="w-20 h-20 bg-gradient-to-tr from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-[0_0_30px_rgba(6,182,212,0.5)]">
                    <FaLocationArrow className="text-white text-3xl" />
                  </div>

                  <h1 className="text-4xl font-black mb-4"><T fr="Forfeo Drops" en="Forfeo Drops" /></h1>

                  <p className="text-slate-400 mb-6 text-sm leading-relaxed">
                    <T fr="Déplace-toi réellement dans la ville pour débloquer des récompenses autour des commerces partenaires." en="Move around the city in real life to unlock rewards near partner businesses." />
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between">
                      <span className="text-slate-400 text-sm"><T fr="Drops actifs" en="Active drops" /></span>
                      <span className="font-black text-cyan-300">
                        {loadingDrops ? '...' : stats.total}
                      </span>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between">
                      <span className="text-slate-400 text-sm"><T fr="Radar" en="Radar" /></span>
                      <span className={`font-black ${isScanning ? 'text-green-400' : 'text-slate-300'}`}>
                        {isScanning ? 'ACTIF' : 'OFF'}
                      </span>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between">
                      <span className="text-slate-400 text-sm"><T fr="Plus proche" en="Closest" /></span>
                      <span className="font-black text-yellow-300">
                        {closestDistance !== null ? formatDistance(closestDistance) : '—'}
                      </span>
                    </div>
                  </div>

                  {!isScanning ? (
                    <button
                      onClick={startRadar}
                      disabled={loadingDrops || drops.length === 0}
                      className="w-full bg-cyan-500 text-slate-900 py-4 rounded-xl font-black uppercase tracking-widest hover:bg-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <T fr="Activer le Radar" en="Activate Radar" />
                    </button>
                  ) : (
                    <button
                      onClick={stopRadar}
                      className="w-full bg-white/10 border border-white/10 text-white py-4 rounded-xl font-black uppercase tracking-widest hover:bg-white/15 transition-all"
                    >
                      <T fr="Arrêter le Radar" en="Stop Radar" />
                    </button>
                  )}

                  {userLocation && (
                    <button
                      onClick={centerOnUser}
                      className="mt-3 w-full bg-white/10 border border-white/10 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-white/15 transition flex items-center justify-center gap-2"
                    >
                      <FaCrosshairs />
                      <T fr="Centrer sur moi" en="Center on me" />
                    </button>
                  )}

                  {error && <p className="text-red-400 text-xs mt-4 font-bold">{error}</p>}

                  {!loadingDrops && drops.length === 0 && (
                    <p className="text-yellow-400 text-xs font-bold mt-4">
                      <T fr="Aucun drop actif n’est disponible pour le moment." en="No active drop is available right now." />
                    </p>
                  )}

                  {userLocation && (
                    <div className="mt-6 p-4 rounded-2xl bg-green-500/10 border border-green-500/20">
                      <div className="flex items-center gap-2 text-green-300 text-sm font-bold">
                        <FaCompass />
                        <T fr="Position détectée" en="Position detected" />
                      </div>
                      <p className="text-[11px] text-slate-400 mt-2 font-mono break-all">
                        {userLocation.lat.toFixed(5)}, {userLocation.lng.toFixed(5)}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="animate-in zoom-in-95 duration-500 sticky top-28">
                  <div className="bg-gradient-to-b from-amber-400 to-orange-600 p-1 rounded-3xl shadow-[0_0_60px_rgba(245,158,11,0.4)]">
                    <div className="bg-slate-900 rounded-[1.4rem] p-8 relative overflow-hidden">
                      <div className="absolute -top-20 -left-20 w-40 h-40 bg-yellow-500 blur-[80px] opacity-40"></div>

                      <div className="w-20 h-20 bg-gradient-to-tr from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-slate-900 shadow-xl relative z-10">
                        <FaUnlockAlt className="text-white text-3xl" />
                      </div>

                      <h2 className="text-sm font-black text-yellow-400 uppercase tracking-widest mb-1 relative z-10 text-center">
                        <T fr="Drop Débloqué !" en="Drop Unlocked!" />
                      </h2>
                      <h3 className="text-3xl font-black text-white mb-2 relative z-10 text-center">
                        {unlockedDrop.title}
                      </h3>

                      <p className="text-slate-400 text-sm mb-3 relative z-10 text-center">
                        Récompense réelle détectée à{' '}
                        <span className="text-white font-bold">
                          {formatDistance(unlockedDrop.currentDistance)}
                        </span>
                      </p>

                      <div className="text-center text-sm text-cyan-300 font-bold mb-2 relative z-10">
                        <FaStore className="inline mr-2" />
                        {getBusinessDisplayName(unlockedDrop)}
                      </div>

                      {getBusinessAddress(unlockedDrop) && (
                        <div className="text-center text-xs text-slate-400 mb-6 relative z-10">
                          {getBusinessAddress(unlockedDrop)}
                        </div>
                      )}

                      <div className="bg-white rounded-2xl p-6 text-slate-900 mb-6 relative z-10">
                        <FaQrcode className="mx-auto text-6xl mb-4 text-slate-800" />
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1 text-center">
                          <T fr="Code à présenter en caisse" en="Code to present at checkout" />
                        </p>
                        <p className="text-2xl font-black font-mono tracking-widest text-orange-600 text-center break-words">
                          {unlockedDrop.reward}
                        </p>
                      </div>

                      {unlockedDrop.description && (
                        <p className="text-xs text-slate-400 italic relative z-10 text-center">
                          {unlockedDrop.description}
                        </p>
                      )}

                      <div className="mt-6 flex items-center justify-center gap-2 text-green-400 text-sm font-bold">
                        <FaCheckCircle />
                        <T fr="Drop validé dans le rayon autorisé" en="Drop validated within the authorized radius" />
                      </div>

                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          onClick={() => openGoogleMapsPin(unlockedDrop)}
                          className="bg-white/10 border border-white/10 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-white/15 transition"
                        >
                          <T fr="Voir sur Maps" en="View on Maps" />
                        </button>
                        <button
                          onClick={() => openGoogleMapsDirections(unlockedDrop)}
                          className="bg-cyan-500 text-slate-900 py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-cyan-400 transition"
                        >
                          <T fr="Itinéraire" en="Directions" />
                        </button>
                      </div>

                      <button
                        onClick={() => {
                          setUnlockedDrop(null);
                          setError('');
                        }}
                        className="absolute top-4 right-4 text-slate-500 hover:text-white"
                      >
                        <FaTimes size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="xl:col-span-9 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-black mb-2">
                    <T fr="Total" en="Total" />
                  </p>
                  <p className="text-3xl font-black text-white">{stats.total}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-black mb-2">
                    <T fr="Débloqués" en="Unlocked" />
                  </p>
                  <p className="text-3xl font-black text-green-400">{stats.unlocked}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-black mb-2">
                    <T fr="Très chauds" en="Very hot" />
                  </p>
                  <p className="text-3xl font-black text-orange-300">{stats.hot}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <p className="text-xs uppercase tracking-widest text-slate-400 font-black mb-2">
                    <T fr="Proches" en="Nearby" />
                  </p>
                  <p className="text-3xl font-black text-cyan-300">{stats.near}</p>
                </div>
              </div>

              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h2 className="text-2xl md:text-3xl font-black flex items-center gap-3">
                  <FaMap className="text-cyan-400" />
                  <T fr="Carte des Drops" en="Drops Map" />
                </h2>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setFilterMode('all')}
                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition ${
                      filterMode === 'all'
                        ? 'bg-cyan-500 text-slate-900'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    }`}
                  >
                    <T fr="Tous" en="All" />
                  </button>
                  <button
                    onClick={() => setFilterMode('unlocked')}
                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition ${
                      filterMode === 'unlocked'
                        ? 'bg-green-500 text-slate-900'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    }`}
                  >
                    <T fr="Débloqués" en="Unlocked" />
                  </button>
                  <button
                    onClick={() => setFilterMode('hot')}
                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition ${
                      filterMode === 'hot'
                        ? 'bg-orange-400 text-slate-900'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    }`}
                  >
                    <T fr="Très chauds" en="Very hot" />
                  </button>
                  <button
                    onClick={() => setFilterMode('near')}
                    className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition ${
                      filterMode === 'near'
                        ? 'bg-blue-400 text-slate-900'
                        : 'bg-white/10 text-white hover:bg-white/15'
                    }`}
                  >
                    <T fr="Proches" en="Nearby" />
                  </button>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden shadow-2xl h-[450px]">
                {!GOOGLE_MAPS_API_KEY ? (
                  <div className="h-full flex items-center justify-center p-8 text-center">
                    <div>
                      <p className="text-xl font-black text-white mb-2">
                        <T fr="Clé Google Maps manquante" en="Missing Google Maps key" />
                      </p>
                      <p className="text-slate-400 text-sm">
                        <T fr="Ajoute" en="Add" /> <span className="font-mono text-cyan-300">VITE_GOOGLE_MAPS_API_KEY</span> dans tes variables d’environnement Render/GitHub.
                      </p>
                    </div>
                  </div>
                ) : loadError ? (
                  <div className="h-full flex items-center justify-center p-8 text-center">
                    <div>
                      <p className="text-xl font-black text-white mb-2">
                        <T fr="Erreur de chargement Google Maps" en="Google Maps loading error" />
                      </p>
                      <p className="text-slate-400 text-sm">
                        <T fr="Vérifie ta clé API et les restrictions Google Cloud." en="Check your API key and Google Cloud restrictions." />
                      </p>
                    </div>
                  </div>
                ) : !isMapLoaded ? (
                  <div className="h-full flex items-center justify-center">
                    <div className="text-cyan-300 font-black animate-pulse">
                      <T fr="Chargement de la carte..." en="Loading map..." />
                    </div>
                  </div>
                ) : (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={userLocation ? 15 : 12}
                    onLoad={(map) => setMapInstance(map)}
                    options={{
                      disableDefaultUI: false,
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: true,
                      styles: [
                        { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
                        { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
                        { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
                        {
                          featureType: 'road',
                          elementType: 'geometry',
                          stylers: [{ color: '#1e293b' }]
                        },
                        {
                          featureType: 'water',
                          elementType: 'geometry',
                          stylers: [{ color: '#0c4a6e' }]
                        }
                      ]
                    }}
                  >
                    {userLocation && (
                      <Marker
                        position={userLocation}
                        title="Votre position"
                        icon={{
                          path: window.google.maps.SymbolPath.CIRCLE,
                          scale: 10,
                          fillColor: '#22c55e',
                          fillOpacity: 1,
                          strokeColor: '#ffffff',
                          strokeWeight: 2
                        }}
                      />
                    )}

                    {filteredDrops.map((drop) => {
                      const coords = getBestCoords(drop);
                      const unlocked = (drop.currentDistance || 999999) <= drop.radius_meters;

                      return (
                        <React.Fragment key={drop.id}>
                          <Marker
                            position={{ lat: coords.lat, lng: coords.lng }}
                            title={drop.title}
                            onClick={() => setSelectedDrop(drop)}
                            icon={{
                              path: window.google.maps.SymbolPath.CIRCLE,
                              scale: 11,
                              fillColor: unlocked ? '#22c55e' : '#06b6d4',
                              fillOpacity: 1,
                              strokeColor: '#ffffff',
                              strokeWeight: 2
                            }}
                          />

                          <Circle
                            center={{ lat: coords.lat, lng: coords.lng }}
                            radius={drop.radius_meters}
                            options={{
                              strokeColor: unlocked ? '#22c55e' : '#06b6d4',
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                              fillColor: unlocked ? '#22c55e' : '#06b6d4',
                              fillOpacity: 0.12
                            }}
                          />
                        </React.Fragment>
                      );
                    })}

                    {selectedDrop && (
                      <InfoWindow
                        position={getBestCoords(selectedDrop)}
                        onCloseClick={() => setSelectedDrop(null)}
                      >
                        <div className="text-slate-900 max-w-[260px]">
                          <h3 className="font-black text-base mb-1">{selectedDrop.title}</h3>

                          <p className="text-xs font-bold text-cyan-700 mb-1">
                            {getBusinessDisplayName(selectedDrop)}
                          </p>

                          {getBusinessAddress(selectedDrop) && (
                            <p className="text-xs text-slate-600 mb-2">
                              {getBusinessAddress(selectedDrop)}
                            </p>
                          )}

                          <p className="text-xs text-slate-600 mb-2">
                            {selectedDrop.description || 'Drop géolocalisé Forfeo'}
                          </p>
                          <p className="text-xs font-bold text-orange-600 mb-2">
                            Code: {selectedDrop.reward}
                          </p>
                          <p className="text-xs font-bold text-slate-700 mb-3">
                            Distance: {formatDistance(selectedDrop.currentDistance)}
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => openGoogleMapsPin(selectedDrop)}
                              className="px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold"
                            >
                              <T fr="Voir" en="View" />
                            </button>
                            <button
                              onClick={() => openGoogleMapsDirections(selectedDrop)}
                              className="px-3 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs font-bold"
                            >
                              <T fr="Aller" en="Go" />
                            </button>
                          </div>
                        </div>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                )}
              </div>

              <div className="flex items-center justify-between">
                <h2 className="text-2xl md:text-3xl font-black flex items-center gap-3">
                  <FaListUl className="text-orange-400" />
                  <T fr="Liste des Drops" en="Drops List" />
                </h2>
                <div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-2">
                  <FaFilter />
                  filtre: {filterMode}
                </div>
              </div>

              {loadingDrops ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="h-48 rounded-3xl bg-white/5 border border-white/10 animate-pulse"
                    />
                  ))}
                </div>
              ) : filteredDrops.length === 0 ? (
                <div className="bg-white/5 border border-white/10 rounded-3xl p-5 md:p-10 text-center">
                  <p className="text-xl font-black text-white mb-2"><T fr="Aucun drop pour ce filtre" en="No drop for this filter" /></p>
                  <p className="text-slate-400 text-sm">
                    <T fr="Essaie un autre filtre ou active le radar pour mettre à jour les distances." en="Try another filter or activate the radar to update distances." />
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredDrops.map((drop) => {
                    const heat = getHeatLabel(drop.currentDistance || 999999, drop.radius_meters);

                    return (
                      <div
                        key={drop.id}
                        className="bg-white/5 border border-white/10 rounded-3xl p-6 shadow-xl hover:border-cyan-400/30 hover:bg-white/[0.07] transition"
                      >
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div>
                            <div
                              className={`inline-flex px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${heat.bg} ${heat.color}`}
                            >
                              {heat.label}
                            </div>
                            <h3 className="text-xl font-black text-white mt-3 leading-tight">
                              {drop.title}
                            </h3>

                            <div className="mt-2 text-sm text-cyan-300 font-bold flex items-center gap-2">
                              <FaStore />
                              {getBusinessDisplayName(drop)}
                            </div>

                            {getBusinessAddress(drop) && (
                              <div className="mt-2 text-xs text-slate-400">
                                {getBusinessAddress(drop)}
                              </div>
                            )}
                          </div>

                          <div className="text-right">
                            <div className="text-cyan-300 font-black text-lg">
                              {formatDistance(drop.currentDistance)}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                              rayon {drop.radius_meters}m
                            </div>
                          </div>
                        </div>

                        <div className="bg-slate-900/60 border border-white/5 rounded-2xl p-4 mb-4">
                          <p className="text-xs uppercase tracking-widest text-slate-500 font-black mb-1">
                            <T fr="Récompense" en="Reward" />
                          </p>
                          <p className="text-orange-300 font-black font-mono break-words">
                            {drop.reward}
                          </p>
                        </div>

                        <p className="text-sm text-slate-400 leading-relaxed min-h-[44px]">
                          {drop.description || 'Récompense géolocalisée exclusive Forfeo.'}
                        </p>

                        <div className="mt-5 space-y-3">
                          <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all"
                              style={{
                                width: `${
                                  drop.currentDistance !== undefined
                                    ? Math.max(
                                        8,
                                        Math.min(
                                          100,
                                          100 - (drop.currentDistance / Math.max(drop.radius_meters * 10, 500)) * 100
                                        )
                                      )
                                    : 8
                                }%`
                              }}
                            />
                          </div>

                          <div className="flex gap-3">
                            <button
                              onClick={() => focusDropOnMap(drop)}
                              className="flex-1 bg-white/10 border border-white/10 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-white/15 transition flex items-center justify-center gap-2"
                            >
                              <FaMapMarkerAlt />
                              <T fr="Focus carte" en="Map focus" />
                            </button>

                            <button
                              onClick={() => openGoogleMapsDirections(drop)}
                              className="flex-1 bg-cyan-500 text-slate-900 py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-cyan-400 transition flex items-center justify-center gap-2"
                            >
                              <FaRoute />
                              <T fr="Itinéraire" en="Directions" />
                            </button>
                          </div>

                          <button
                            onClick={() => openGoogleMapsPin(drop)}
                            className="w-full bg-white/5 border border-white/10 text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-white/10 transition flex items-center justify-center gap-2"
                          >
                            <FaExternalLinkAlt />
                            <T fr="Ouvrir dans Google Maps" en="Open in Google Maps" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {isScanning && !unlockedDrop && (
                <div className="mt-8 bg-cyan-500/10 border border-cyan-500/20 rounded-3xl p-6">
                  <div className="flex items-center gap-3 text-cyan-300 font-black mb-2">
                    <FaSearch />
                    <T fr="Radar en cours" en="Radar running" />
                  </div>
                  <p className="text-slate-300 text-sm">
                    <T fr="Continue à marcher. Les distances se mettent à jour automatiquement et un drop se débloquera dès que tu entres dans son rayon." en="Keep walking. Distances update automatically and a drop will unlock as soon as you enter its radius." />
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
